"use client";

import { useSession } from "next-auth/react";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/hooks/useAuth";
import { getFirebaseAuth } from "@/lib/api/firebase-ultra-safe";
import { signInWithCustomToken } from "firebase/auth";
import { RegionSelectModal } from "./RegionSelectModal";

export default function NextAuthToFirebaseSync() {
  const { data: session, status, update: updateSession } = useSession();
  const { user: firebaseUser, refreshUser } = useAuth();
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  const [hasSynced, setHasSynced] = useState(false); // 동기화 완료 여부
  const [showRegionModal, setShowRegionModal] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);

  useEffect(() => {
    const createGoogleUserProfile = async () => {
      // NextAuth 세션이 있고 Firebase Auth에 사용자가 없는 경우
      // 이미 동기화했으면 건너뜀
      if (session?.user && !firebaseUser && !isCreatingProfile && !hasSynced && status === "authenticated") {
        try {
          setIsCreatingProfile(true);
          console.log("🔄 NextAuth → Firebase DB 프로필 생성 시작 (서버 사이드 API 사용)");
          
          // 서버 사이드 API를 통해 프로필 생성 (더 빠르고 안정적)
          const userEmail = session.user.email;
          const userName = session.user.name || userEmail?.split("@")[0] || "사용자";
          const userImage = session.user.image;
          const userId = session.user.id || `google_${userEmail?.replace("@", "_").replace(".", "_")}`;
          
          try {
            // 1단계: Firebase Custom Token 가져오기
            const tokenResponse = await fetch('/api/auth/firebase-token', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
            });
            
            if (!tokenResponse.ok) {
              console.error("❌ Firebase Custom Token 가져오기 실패:", await tokenResponse.json());
            } else {
              const tokenResult = await tokenResponse.json();
              if (tokenResult.success && tokenResult.customToken) {
                // 2단계: Firebase Auth에 로그인
                try {
                  const auth = getFirebaseAuth();
                  if (auth) {
                    await signInWithCustomToken(auth, tokenResult.customToken);
                    console.log("✅ Firebase Auth 로그인 성공:", tokenResult.uid);
                  }
                } catch (firebaseAuthError: any) {
                  console.error("❌ Firebase Auth 로그인 실패:", firebaseAuthError?.message);
                }
              }
            }
            
            // 3단계: Firestore 프로필 동기화
            const response = await fetch('/api/auth/sync-profile', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
              body: JSON.stringify({
                uid: userId,
                email: userEmail,
                name: userName,
                image: userImage,
                provider: 'google',
                providerId: session.user.id,
              }),
            });
            
            if (response.ok) {
              const result = await response.json();
              console.log("✅ 서버 사이드 API로 프로필 동기화 완료", result);
              
              // 신규 사용자 여부 확인 (프로필이 새로 생성되었는지)
              const wasNewUser = result.isNewUser || false;
              setIsNewUser(wasNewUser);
              
              // 동기화 완료 플래그 설정 (재시도 방지)
              setHasSynced(true);
              
              // 프로필 동기화 성공 - useAuth가 자동으로 프로필을 로드함
              // 페이지 새로고침은 하지 않음 (무한 루프 방지)
              try {
                await updateSession();
                console.log("✅ NextAuth 세션 업데이트 완료 - useAuth가 자동으로 프로필 로드함");
              } catch (updateError) {
                console.warn("⚠️ 세션 업데이트 실패 (무시됨):", updateError);
              }
              
              return;
            } else {
              // 에러 응답 확인
              const errorData = await response.json().catch(() => ({ error: "알 수 없는 오류" }));
              console.error("❌ 프로필 동기화 API 실패:", {
                status: response.status,
                statusText: response.statusText,
                error: errorData,
              });
              return;
            }
          } catch (apiError: any) {
            // 서버 사이드 API 실패 시 에러 로깅
            console.error("❌ 서버 사이드 API 호출 실패:", {
              message: apiError?.message,
              stack: apiError?.stack,
            });
            return;
          }
        } finally {
          setIsCreatingProfile(false);
        }
      }
    };

    if (status === "authenticated") {
      createGoogleUserProfile();
    }
  }, [session, firebaseUser, status, isCreatingProfile, hasSynced, updateSession]);

  // 프로필이 로드된 후 region이 없으면 모달 표시
  useEffect(() => {
    if (
      firebaseUser &&
      hasSynced &&
      isNewUser &&
      (!firebaseUser.region || firebaseUser.region.trim() === "") &&
      !showRegionModal
    ) {
      // 약간의 딜레이를 주어 프로필이 완전히 로드되도록
      const timer = setTimeout(() => {
        setShowRegionModal(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [firebaseUser, hasSynced, isNewUser, showRegionModal]);

  const handleRegionComplete = async () => {
    setShowRegionModal(false);
    setIsNewUser(false);
    // 프로필 새로고침
    if (refreshUser) {
      await refreshUser();
    }
  };

  return (
    <>
      {showRegionModal && firebaseUser && (
        <RegionSelectModal
          isOpen={showRegionModal}
          userId={firebaseUser.uid}
          onComplete={handleRegionComplete}
        />
      )}
    </>
  );
}
